
import { defineComponent, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAgreementModule, AgreementType } from '@/modules/agreements'
import AgreementUpdateAgreementManagement
    from '@/components/agreements/agreement-management/agreement-management-controls.vue'
import { AgreementsRouteNames, InvoicesRouteNames } from '@/router/routes-names'
import AgreementsEditableFieldPrice from '@/components/agreements/editable-fields/price.vue'
import AgreementsEditableFieldName from '@/components/agreements/editable-fields/name.vue'
import AgreementsEditableFieldMaxMembersAllowed from '@/components/agreements/editable-fields/max-members-allowed.vue'
import AgreementsOpenDialogFieldPackages from '@/components/agreements/open-dialog-fields/packages.vue'
import { useOrganizationModule } from '@/modules/organizations'
import AgreementFormFieldMarketingCommunication from '@/components/agreements/form-fields/marketing-communication.vue'
import { useInvoicesModule } from '@/modules/invoices'
import AgreementStatusBadge from '@/components/agreements/agreement-status-badge.vue'
import AgreementsEditableFieldExcludeDigitalClassrooms
    from '@/components/agreements/editable-fields/exclude-digital-classrooms.vue'
import AgreementsEditableFieldAdditionalInfo from '@/components/agreements/editable-fields/additional-info.vue'
import { useAxiosModule } from '@/modules/_shared/axios'
import AgreementsEditableFieldInvoicingAllowedFrom
    from '@/components/agreements/editable-fields/invoicing-allowed-from.vue'
import AgreementsEditableFieldEndDate from '@/components/agreements/editable-fields/end-date.vue'
import AgreementsOpenDialogFieldScheduledPrices from '@/components/agreements/open-dialog-fields/scheduled-prices.vue'
import AgreementsEditableFieldLspExport from '@/components/agreements/editable-fields/LSP-export.vue'
import AgreementsOpenDialogContactPersons from '@/components/agreements/open-dialog-fields/contact-persons.vue'
import AgreementFormFieldEditableInSelfService from '@/components/agreements/form-fields/editable-in-self-service.vue'

const { getDownloadFromApi } = useAxiosModule()

export default defineComponent({
    name: 'view-agreements-show-agreement',
    components: {
        AgreementsEditableFieldLspExport,
        AgreementsOpenDialogContactPersons,
        AgreementsEditableFieldEndDate,
        AgreementsEditableFieldInvoicingAllowedFrom,
        AgreementsOpenDialogFieldScheduledPrices,
        AgreementsEditableFieldAdditionalInfo,
        AgreementsEditableFieldExcludeDigitalClassrooms,
        AgreementStatusBadge,
        AgreementUpdateAgreementManagement,
        AgreementsEditableFieldPrice,
        AgreementsEditableFieldName,
        AgreementsEditableFieldMaxMembersAllowed,
        AgreementsOpenDialogFieldPackages,
        AgreementFormFieldMarketingCommunication,
        AgreementFormFieldEditableInSelfService
    },
    async setup () {
        const route = useRoute()
        const router = useRouter()
        const { getAgreement, currentAgreement, getAgreementScheduledPrices, agreementScheduledPrices } = useAgreementModule()
        const { setInvoicesAgreementFilter } = useInvoicesModule()
        const { getOrganizationsSimplified, organizationsSimplifiedByOrganizationId } = useOrganizationModule()

        await getAgreement(route.params.agreementId as string)
        await getAgreementScheduledPrices(route.params.agreementId as string)

        if (organizationsSimplifiedByOrganizationId.value.size === 0) {
            await getOrganizationsSimplified()
        }

        const agreementModel = ref(currentAgreement)
        const scheduledPricesKey = ref(0)

        const isAgreementExpired = computed(() => agreementModel.value.status === 'expired')

        const agreement = computed(() => {
            return {
                ...agreementModel.value,
                organization: organizationsSimplifiedByOrganizationId.value.get(String(agreementModel.value.organizationId))?.companyName,
                type: agreementModel.value.type + ' pricing',
                price: agreementModel.value.price * 0.01,
                minLength: agreementModel.value.minimalLength + ' month(s)',
                cancelPeriod: agreementModel.value.cancelPeriod + ' month(s)',
                invoicingPeriod: agreementModel.value.invoicingPeriod + ' month(s)'
            }
        })

        const membersModel = ref({ label: 'All members', value: 'all' })
        const membersOptions = [
            { label: 'All members', value: 'all' },
            { label: 'Only inactive members', value: 'inactive' },
            { label: 'Only active members', value: 'active' }
        ]
        const downloadMemberListDisabled = ref(false)
        function onGoToList () {
            router.push({ name: AgreementsRouteNames.LIST, query: { page: route.query.page }, params: { restoreFilters: 1 } })
        }

        function onShowAgreementInvoices () {
            setInvoicesAgreementFilter(String(agreementModel.value.agreementId))
            router.push({ name: InvoicesRouteNames.LIST, query: { page: route.query.page } })
        }

        function onDownloadMembersListClick () {
            downloadMemberListDisabled.value = true
            getDownloadFromApi(
                `agreements/${agreementModel.value.agreementId}/members-export/${membersModel.value.value}`,
                `members-${agreementModel.value.agreementId}.xlsx`
            )
            setTimeout(() => {
                downloadMemberListDisabled.value = false
            }, 3000)
        }

        async function onPricesUpdated () {
            await getAgreementScheduledPrices(route.params.agreementId as string)
            scheduledPricesKey.value++
        }

        const packageNames = computed(() => agreementModel.value.packages.map(p => p.name))
        const packageIds = computed(() => agreementModel.value.packages.map(p => p.packageId))

        const contactPersons = computed(() => agreementModel.value.contacts)

        const agreementModelPrice = computed(() => {
            const price = agreementModel.value.price * 0.01
            if (Number.isInteger(price)) return `${price} €`
            return Number(price).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' })
        })

        return {
            agreement,
            membersModel,
            membersOptions,
            onGoToList,
            AgreementsRouteNames,
            agreementModel,
            AgreementType,
            onShowAgreementInvoices,
            onDownloadMembersListClick,
            downloadMemberListDisabled,
            isAgreementExpired,
            packageNames,
            packageIds,
            contactPersons,
            agreementModelPrice,
            agreementScheduledPrices,
            scheduledPricesKey,
            onPricesUpdated
        }
    }
}
)
